import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Progress,
} from "reactstrap";
import { GAP_BASE_URL, formatRatio, useBackgroundAnalysis } from "./gapCompetitionUtils";

function Form({ children, ...props }) {
  return <form {...props}>{children}</form>;
}

function SkillChips({ skills, color }) {
  if (!skills || skills.length === 0) return <span className="text-muted">None</span>;
  return (
    <div>
      {skills.map((s, i) => {
        // The API returns skills as plain strings in most sections (objective,
        // market insights, employee matched/missing skills) but as {id, label}
        // objects elsewhere — support both rather than assuming one shape.
        const label = typeof s === "string" ? s : s?.label || s?.id || String(s);
        const key = typeof s === "string" ? s : s?.id || s?.label || i;
        return (
          <Badge key={key} color={color} pill className="mr-1 mb-1" style={{ fontWeight: 400 }}>
            {label}
          </Badge>
        );
      })}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <Col xs="6" md="3" className="mb-3">
      <Card className="mb-0" style={{ height: "100%" }}>
        <CardBody className="text-center">
          <div style={{ fontSize: "1.75rem", fontWeight: 600, color: "#2d3e75" }}>{value}</div>
          <div className="text-muted" style={{ fontSize: "0.85rem" }}>{label}</div>
        </CardBody>
      </Card>
    </Col>
  );
}

function EmployeeScoreCard({ employee, threshold }) {
  if (!employee) return null;
  const readinessPct = Math.round((employee.readiness_score || 0) * 100);
  const overlapPct = Math.round((employee.overlap_ratio || 0) * 100);
  const similarityPct = Math.round((employee.role_similarity || 0) * 100);
  const thresholdPct = Math.round((threshold || 0) * 100);
  const meetsThreshold = readinessPct >= thresholdPct;
  return (
    <Card className="mb-0" style={{ border: "1px solid #eee" }}>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <strong>{employee.name}</strong>
            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
              {employee.current_role}{employee.current_role ? " · " : ""}ID: {employee.employee_id}
            </div>
          </div>
          <Badge color={meetsThreshold ? "success" : "warning"} pill>
            Readiness {readinessPct}% <span style={{ fontWeight: 400 }}>(threshold {thresholdPct}%)</span>
          </Badge>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between" style={{ fontSize: "0.8rem" }}>
            <span>Readiness Score</span>
            <span>{readinessPct}%</span>
          </div>
          <Progress value={readinessPct} color={meetsThreshold ? "success" : "warning"} />
        </div>

        <Row>
          <Col md="6" className="mb-3">
            <div className="d-flex justify-content-between" style={{ fontSize: "0.8rem" }}>
              <span>Skill Overlap</span>
              <span>{overlapPct}%</span>
            </div>
            <Progress value={overlapPct} color="info" />
          </Col>
          <Col md="6" className="mb-3">
            <div className="d-flex justify-content-between" style={{ fontSize: "0.8rem" }}>
              <span>Role Similarity</span>
              <span>{similarityPct}%</span>
            </div>
            <Progress value={similarityPct} color="secondary" />
          </Col>
        </Row>

        <Row>
          <Col md="6" className="mb-2">
            <h6 className="text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}>
              Matched Skills
            </h6>
            <SkillChips skills={employee.matched_skills} color="success" />
          </Col>
          <Col md="6" className="mb-2">
            <h6 className="text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}>
              Missing Skills
            </h6>
            <SkillChips skills={employee.missing_skills} color="danger" />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

function CandidateCard({ candidate, highlight }) {
  if (!candidate) return null;
  return (
    <Card className="mb-0" style={{ border: highlight ? "1px solid #6bd098" : "1px solid #eee" }}>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <strong>{candidate.name}</strong>
            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
              {candidate.current_role} · {candidate.source}
            </div>
          </div>
          <Badge color="info" pill>
            {formatRatio(candidate.match_ratio)} match
          </Badge>
        </div>
        <SkillChips skills={candidate.matched_skills} color="secondary" />
      </CardBody>
    </Card>
  );
}

function WorkforceGapTab({ departments, loadingDepartments }) {
  const [departmentId, setDepartmentId] = useState("");
  const [marketPageSize, setMarketPageSize] = useState(100);
  const [topMarketSkills, setTopMarketSkills] = useState(12);
  const [reskillThreshold, setReskillThreshold] = useState(0.6);

  const { loading, pendingMessage, error, setError, result, run } = useBackgroundAnalysis();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!departmentId) {
      setError("Please select a department.");
      return;
    }
    await run(
      `${GAP_BASE_URL}/workforce-gap-recommendation`,
      {
        department_id: Number(departmentId),
        market_page_size: Number(marketPageSize),
        top_market_skills: Number(topMarketSkills),
        reskill_threshold: Number(reskillThreshold),
      },
      "Failed to generate a recommendation. Please check the parameters and try again."
    );
  };

  const isExternalHire = result?.recommendation?.action === "external_hire";
  const hiringProfile = result?.recommendation?.external_hiring_profile;

  return (
    <Row>
      <Col md="12">
        <Card>
          <CardHeader>
            <CardTitle tag="h4" className="mb-0">
              Workforce Gap Recommendation
            </CardTitle>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Get a reskill-or-hire recommendation for a department based on market demand and your team's
              current skills.
            </p>
          </CardHeader>
          <CardBody>
            <Form onSubmit={handleSubmit} className="mb-2">
              <Row className="align-items-end">
                <Col md="3" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label for="wgDepartment">Department</Label>
                    <Input
                      type="select"
                      id="wgDepartment"
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                      disabled={loadingDepartments}
                    >
                      <option value="">{loadingDepartments ? "Loading departments…" : "Select a department"}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label for="wgMarketPageSize">Market Page Size</Label>
                    <Input
                      type="number"
                      id="wgMarketPageSize"
                      min="1"
                      max="1000"
                      value={marketPageSize}
                      onChange={(e) => setMarketPageSize(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="2" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label for="wgTopMarketSkills">Top Market Skills</Label>
                    <Input
                      type="number"
                      id="wgTopMarketSkills"
                      min="1"
                      max="100"
                      value={topMarketSkills}
                      onChange={(e) => setTopMarketSkills(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="3" className="mb-3">
                  <FormGroup className="mb-0">
                    <Label for="wgThreshold">
                      Reskill Threshold ({Math.round(reskillThreshold * 100)}%)
                    </Label>
                    <Input
                      type="range"
                      id="wgThreshold"
                      min="0"
                      max="1"
                      step="0.05"
                      value={reskillThreshold}
                      onChange={(e) => setReskillThreshold(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="2" className="mb-3">
                  <Button color="info" type="submit" disabled={loading} block>
                    {loading ? <Spinner size="sm" /> : "Get Recommendation"}
                  </Button>
                </Col>
              </Row>
            </Form>

            {pendingMessage && (
              <Alert color="info" className="d-flex align-items-center">
                <Spinner size="sm" className="mr-2" />
                <span>{pendingMessage}</span>
              </Alert>
            )}
            {error && <Alert color="danger">{error}</Alert>}
          </CardBody>
        </Card>
      </Col>

      {result && (
        <>
          <Col md="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <CardTitle tag="h4" className="mb-0">
                    Target Role: {result.target_role?.label || "—"}
                  </CardTitle>
                  <Badge color={isExternalHire ? "warning" : "success"} pill style={{ fontSize: "0.85rem" }}>
                    {isExternalHire ? "External Hire Recommended" : "Reskill Recommended"}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <p className="mb-3">{result.recommendation?.reason}</p>
                <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                  Target Skills
                </h6>
                <SkillChips skills={result.objective?.skills} color="info" />
              </CardBody>
            </Card>
          </Col>

          <Col md="12">
            <Row>
              <StatTile label="Jobs Analyzed" value={result.market_insights?.jobs_analyzed ?? "—"} />
              <StatTile
                label="Total Market Matches"
                value={result.market_insights?.total_market_matches?.toLocaleString?.() ?? "—"}
              />
              <StatTile label="Top Market Skills" value={result.market_insights?.top_market_skills?.length ?? 0} />
              <StatTile label="Gap Skills" value={result.market_insights?.gap_skills?.length ?? 0} />
              <StatTile label="Company Employees" value={result.company_snapshot?.employee_count ?? "—"} />
            </Row>
          </Col>

          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  Market Insights
                </CardTitle>
                {result.market_insights?.query_keywords?.length > 0 && (
                  <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                    Searched for: {result.market_insights.query_keywords.join(", ")}
                  </p>
                )}
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4" className="mb-3">
                    <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                      Top Market Skills
                    </h6>
                    <SkillChips skills={result.market_insights?.top_market_skills} color="info" />
                  </Col>
                  <Col md="4" className="mb-3">
                    <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                      Gap Skills (not covered internally)
                    </h6>
                    <SkillChips skills={result.market_insights?.gap_skills} color="danger" />
                  </Col>
                  <Col md="4" className="mb-3">
                    <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                      Company Covered Skills
                    </h6>
                    <SkillChips skills={result.company_snapshot?.covered_skills} color="secondary" />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  {isExternalHire ? "Top Internal Candidate" : "Recommended Employee"}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <EmployeeScoreCard
                  employee={result.recommendation?.recommended_employee}
                  threshold={reskillThreshold}
                />
              </CardBody>
            </Card>
          </Col>

          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  Employees Considered
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                  <Table hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Current Role</th>
                        <th className="text-right">Skill Overlap</th>
                        <th className="text-right">Role Similarity</th>
                        <th className="text-right">Readiness</th>
                        <th>Matched Skills</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.employees || []).map((emp) => {
                        const isRecommended =
                          result.recommendation?.recommended_employee?.employee_id === emp.employee_id;
                        return (
                          <tr key={emp.employee_id} style={isRecommended ? { background: "#f4fff8" } : undefined}>
                            <td>
                              {emp.name}
                              {isRecommended && (
                                <Badge color="success" pill className="ml-2" style={{ fontSize: "0.65rem" }}>
                                  Recommended
                                </Badge>
                              )}
                            </td>
                            <td>{emp.current_role}</td>
                            <td className="text-right">{formatRatio(emp.overlap_ratio)}</td>
                            <td className="text-right">{formatRatio(emp.role_similarity)}</td>
                            <td className="text-right">{formatRatio(emp.readiness_score)}</td>
                            <td>
                              <SkillChips skills={emp.matched_skills} color="secondary" />
                            </td>
                          </tr>
                        );
                      })}
                      {(!result.employees || result.employees.length === 0) && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No employees were evaluated.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>

          {isExternalHire && hiringProfile && (
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h5" className="mb-0">
                    External Hiring Profile — {hiringProfile.role}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                    Priority Skills
                  </h6>
                  <div className="mb-3">
                    <SkillChips skills={hiringProfile.priority_skills} color="danger" />
                  </div>

                  {hiringProfile.recommended_candidate && (
                    <>
                      <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                        Recommended Candidate
                      </h6>
                      <div className="mb-3">
                        <CandidateCard candidate={hiringProfile.recommended_candidate} highlight />
                      </div>
                    </>
                  )}

                  <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                    Top Candidates
                  </h6>
                  <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                    <Table hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Current Role</th>
                          <th>Source</th>
                          <th className="text-right">Match</th>
                          <th>Matched Skills</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(hiringProfile.top_candidates || []).map((c) => (
                          <tr key={c.profile_id}>
                            <td>{c.name}</td>
                            <td>{c.current_role}</td>
                            <td>{c.source}</td>
                            <td className="text-right">{formatRatio(c.match_ratio)}</td>
                            <td>
                              <SkillChips skills={c.matched_skills} color="secondary" />
                            </td>
                          </tr>
                        ))}
                        {(!hiringProfile.top_candidates || hiringProfile.top_candidates.length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No external candidates found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </>
      )}
    </Row>
  );
}

export default WorkforceGapTab;
